import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../../services/authApi';
import { useAuth } from '../../contexts/AuthContext';

/**
 * A guest giving their profile an email and a password, wherever they do
 * it: the saved account replaces the guest in the session, and the error
 * says what to do next (an email that already has an account included).
 */
export const useCompleteAccount = () => {
  const { t } = useTranslation('auth');
  const { applyUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const complete = useCallback(
    async (email: string, password: string): Promise<boolean> => {
      setIsSaving(true);
      setError(null);
      try {
        applyUser(await authApi.completeAccount({ email: email.trim(), password }));
        return true;
      } catch (err: any) {
        const message = err?.response?.data?.message;
        if (err?.response?.status === 409 || message === 'EMAIL_TAKEN') {
          setError(t('guest.emailTaken'));
        } else if (Array.isArray(message)) {
          setError(message.join(' '));
        } else {
          setError(message || t('guest.failed'));
        }
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [applyUser, t],
  );

  const reset = useCallback(() => setError(null), []);

  return { complete, error, isSaving, reset };
};
